import React, { useState, UIEvent, PropsWithChildren } from "react";
import { Box, Divider } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IKeyFiguresData } from "src/api/server/extended-search.types";
import { handleResetKeyFigures } from "src/components/pages/search/store/actions";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { Button } from "src/components-mui/base/button/button";
import { KeyFiguresGroup } from "./components/key-figures-group";

interface IScrollableBlurContainerProps extends PropsWithChildren {
    footer: React.ReactNode;
}

const ScrollableBlurContainer = ({ footer, children }: IScrollableBlurContainerProps) => {
    const [isBottomBlurVisible, setIsBottomBlurVisible] = useState(true);

    const handleScrollForBlur = (event: UIEvent) => {
        const element = event.target as Element;
        const THRESHOLD = 30;
        setIsBottomBlurVisible(element.scrollTop <= THRESHOLD);
    };

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                onScroll={handleScrollForBlur}
                sx={{
                    height: "480px",
                    overflow: "auto",
                    p: 3,
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    scrollbarWidth: "thin",
                    pr: 1,
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-track": { background: "white" },

                    "&::-webkit-scrollbar-thumb": {
                        border: "4px solid white",
                        borderRadius: 1,
                        backgroundColor: "gray.200",
                    },
                }}
            >
                {children}
            </Box>

            {isBottomBlurVisible && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "448px",
                        height: "32px",
                        width: "calc(100% - 16px)",
                        ml: 1,
                        background: "linear-gradient(180deg, #fff -4.68%, rgba(255,255,255,0.6) 33.79%, #fff 79.69%)",
                        pointerEvents: "none",
                    }}
                />
            )}

            {footer}
        </Box>
    );
};

interface IKeyFiguresFilterBodyContentProps {
    keyFiguresData: IKeyFiguresData;
}

export const KeyFiguresFilterBodyContent = ({ keyFiguresData }: IKeyFiguresFilterBodyContentProps) => {
    const { keyFiguresGroups, faqLink } = keyFiguresData;
    const { t } = useTranslation("extended-search");

    const handleReset = () => {
        handleResetKeyFigures();
    };

    return (
        <ScrollableBlurContainer
            footer={
                <Box
                    sx={{
                        p: 3,
                        pt: 0,
                    }}
                >
                    <Divider sx={{ borderColor: "gray.100", mb: 2 }} />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Button variant="ghost-underline" size="small" sx={{ ml: -2 }} onClick={handleReset}>
                            {t("buttons.reset-key-figures")}
                        </Button>
                        {faqLink && (
                            <ArrowLink
                                sx={{ display: "inline-flex", alignItems: "center", fontSize: "sm" }}
                                href={faqLink.url}
                                target={faqLink.target}
                            >
                                {faqLink.name}
                            </ArrowLink>
                        )}
                    </Box>
                </Box>
            }
        >
            {keyFiguresGroups.map(keyFiguresGroup => (
                <KeyFiguresGroup key={keyFiguresGroup.headline} keyFiguresGroup={keyFiguresGroup} />
            ))}
        </ScrollableBlurContainer>
    );
};
